import React, { useEffect, useRef, useState, useMemo } from "react";
import { useExam } from "../context/ExamContext";
import stringQuestionIntervalToArray from "../utils/stringQuestionIntervalToArray"
import { useExamServerDataOperations } from "./useExamServerDataOperations";

export function useNavigation({ sectionData, section, stageQuestionInterval, updateBulkData }) {
    const {
        activeQuestion,
        setActiveQuestion,
        changeStage,
        goNextStage,
        backPreviousStage,
        stage,
        answers,
        activeView,
        writingAnswers
    } = useExam();

    const defaultStageQuestionInterval = "";
    const currentStageQuestionInterval = stageQuestionInterval || defaultStageQuestionInterval;
    const intervalFirstNumber = currentStageQuestionInterval ? Number(currentStageQuestionInterval.split("-")[0]) : 1;
    const sectionUnitLabel = section == "reading" ? "passage" : section == "listening" ? "part" : section == "writing" ? "question" : null
    const questionCount = ["listening", "reading"].includes(section)
        ? 40
        : section === "writing"
            ? 2
            : null;


    const skipResetRef = useRef(true);

    // const answersRef = useRef(answers);
    useEffect(() => {
        if (currentStageQuestionInterval) {
            const lastAnswer = answers.at(-1);
            let doesBelongToCurrentPassage;
            if (lastAnswer != undefined && lastAnswer != null) {
                const stageQuestionNumbers = stringQuestionIntervalToArray(sectionData?.[`${sectionUnitLabel}${stage}`]?.totalQuestionInterval)
                doesBelongToCurrentPassage = stageQuestionNumbers.some(num =>
                    lastAnswer.questionNumber == num
                );
            }

            if (skipResetRef.current) {

                setActiveQuestion(doesBelongToCurrentPassage ? lastAnswer?.questionNumber : intervalFirstNumber);
                window.scrollTo(0, 0)
                skipResetRef.current = false;
            }
        }
    }, [stage]);

    //    WHEN SECTION CHANGES RE-RENDERS
    useEffect(() => {
        handleStage(stage)
        setActiveQuestion(intervalFirstNumber)
    }, [sectionData]);


    // useEffect(() => {
    //     answersRef.current = answers;
    // }, [answers]);


    //TO TRIGGER SAVE TIMER TO START
    const triggerServerDataUpdate = useExamServerDataOperations({ autoSave: false });

    //WE DEFINE IF ALL STAGE QUESTIONS DONE OR NOT
    const isStageDone = (passageNum) => {
        if (sectionData?.[`${sectionUnitLabel}${passageNum}`]) {
            const stageQuestionNumbers = stringQuestionIntervalToArray(sectionData?.[`${sectionUnitLabel}${passageNum}`]?.totalQuestionInterval)
            return stageQuestionNumbers.every(num =>
                answers.some(answer =>
                    answer.questionNumber === num &&
                    answer.answer != null &&
                    answer.answer !== ""
                )
            );
        }
        return false;
    }

    //WE SIMPLFY TO SWITCH BETWEEN STAGES
    const stageCompletionStatus = useMemo(() => ({
        1: isStageDone(1),
        2: isStageDone(2),
        3: isStageDone(3),
    }), [sectionData, answers]);

    //WE DIRECTLY CHANGE STAGE 
    // const handleStage = (stageNum) => {
    //     changeStage(stageNum);
    //     window.scrollTo(0, 0);
    // };


    const handleStage = (stageNum) => {
        // mark that *this* stage change was manual
        skipResetRef.current = true;
        changeStage(stageNum);
        window.scrollTo(0, 0);
    };

    //HANDLE PREVIOUS QUESTION OR STAGE IF IT IS LAST OR FIRST QUESTION
    const handlePrevQuestion = () => {
        if (activeQuestion > Number(currentStageQuestionInterval.split("-")[0])) {
            setActiveQuestion((prev) => prev - 1);
        } else {
            setActiveQuestion((prev) => prev - 1);
            backPreviousStage();
        }
        window.scrollTo(0, 0);
    };


    const handleNextQuestion = () => {
        if (activeQuestion < Number(currentStageQuestionInterval.split("-")[1])) {
            setActiveQuestion(prev => prev + 1);
        } else if (activeQuestion === Number(currentStageQuestionInterval.split("-")[1])) {
            setActiveQuestion(prev => prev + 1);
            triggerServerDataUpdate();
            goNextStage();
        } else if (activeQuestion === questionCount) {
            triggerServerDataUpdate();
            goNextStage();
        }
        window.scrollTo(0, 0);
    };

    //HANDLE QUESTION CLICK TO MAKE IT ACTIVE OR CHANGE ITS STAGE ACCORDINGLY
    const handleQuestion = (number) => {
        setActiveQuestion(number)
        let stage;
        const maxStages = section === 'reading' ? 3
            : section === 'listening' ? 4
                : 0;

        if (["reading", "listening"].includes(section)) {
            if (maxStages > 0) {
                if (maxStages > 0) {
                    for (let index = 1; index <= maxStages; index++) {
                        const stageQuestionNumbers = stringQuestionIntervalToArray(sectionData?.[`${sectionUnitLabel}${index}`]?.totalQuestionInterval)
                        stage = stageQuestionNumbers.find(num => num == number);
                        if (stage) {
                            changeStage(index);
                        }
                    }
                }
            }
        }
    }

    //TO IDENTIFY WHETHER IT IS ANSWERED OR NOT
    const isAnswered = (number) => {
        return answers.some((answer) =>
            Number(answer.questionNumber) === number &&
            answer.answer != null &&
            answer.answer !== ""
        );
    };

    return {
        activeQuestion,
        stage,
        questionCount,
        stageCompletionStatus,
        handlePrevQuestion,
        handleStage,
        section,
        sectionUnitLabel,
        handleNextQuestion,
        handleQuestion,
        isAnswered
    }
};